import { HttpContextToken, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http'
import { catchError, switchMap } from 'rxjs/operators'
import { Observable, throwError } from 'rxjs'
import { MessageService } from 'primeng/api'
import { Injectable } from '@angular/core'
import { Router } from '@angular/router'

import { AuthService } from '../../data/services/modules/auth/auth.service'

/**
 * Marca de «esta petición ya se reintentó tras refrescar». Va en el `HttpContext` y no en una
 * cabecera: el contexto NO viaja por la red, así que no se puede falsificar desde fuera ni acaba
 * ensuciando la petición real.
 */
const YA_REINTENTADA = new HttpContextToken<boolean>(() => false)

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
  constructor(
    private messageService: MessageService,
    private authService: AuthService,
    private router: Router,
  ) {}

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const isUploadRequest = request.url.includes('documents/upload')

    return next.handle(request).pipe(
      catchError((err) => {
        if (isUploadRequest) {
          console.error('❌ [UPLOAD DEBUG] - Error en upload:', {
            status: err.status,
            statusText: err.statusText,
            url: err.url,
          })
        }

        if (request.url !== '/login') {
          if (err.status === 401) {
            //! B-044 · Un 401 ya NO cierra la sesión de inmediato. Antes sí, y por eso la plataforma
            //! mandaba al login todo el tiempo: bastaba **un solo** 401 —el que fuera— para tirar una
            //! sesión que tenía días de vida por delante en su token de refresco.
            //!
            //! Ahora se intenta renovar contra `/auth/refresh` y se repite la petición. Solo si esa
            //! renovación falla se cierra: ahí la sesión sí murió de verdad (el refresco venció, o al
            //! usuario lo dieron de baja) y mandarlo al login es lo correcto.
            if (this.puedeIntentarRenovar(request)) return this.renovarYReintentar(request, next, err)

            this.cerrarSesion()
          } else if (err.status === 403) {
            //* El usuario no tiene permisos para realizar esta acción o acceder a esta página
            this.messageService.add({
              severity: 'error',
              summary: 'Sin Permisos',
              detail: 'No cuenta con los permisos necesarios para realizar esta acción.',
            })
          }
        }
        return throwError(err)
      }),
    )
  }

  /**
   * Un 401 que vale la pena reintentar. Se descartan tres casos, y los tres por el mismo motivo:
   * reintentarlos daría vueltas sin llegar a nada.
   *
   * - **Sin sesión**: no hay token de refresco con el que renovar.
   * - **Los propios endpoints de sesión**: un 401 de `/auth/login` es «credenciales incorrectas» y uno
   *   de `/auth/refresh` es «la sesión murió». Reintentar el refresco con el refresco sería un bucle.
   * - **Ya reintentada**: se renovó, se repitió, y volvió a dar 401. La sesión no se recupera.
   */
  private puedeIntentarRenovar(request: HttpRequest<any>): boolean {
    if (!this.authService.getUser?.detailToken?.refreshToken) return false
    if (request.url.includes('/auth/login') || request.url.includes('/auth/refresh')) return false
    return !request.context.get(YA_REINTENTADA)
  }

  private renovarYReintentar(request: HttpRequest<any>, next: HttpHandler, errorOriginal: any): Observable<HttpEvent<any>> {
    return this.authService.refrescarSesion().pipe(
      switchMap((renovada) => {
        if (!renovada) {
          this.cerrarSesion()
          return throwError(errorOriginal)
        }

        const usuario = this.authService.getUser
        const reintento = request.clone({
          //* La marca viaja por si algún interceptor de más abajo vuelve a pasar por aquí; el candado
          //* de verdad es el `catchError` de la línea siguiente.
          context: request.context.set(YA_REINTENTADA, true),
          setHeaders: {
            Authorization: `Bearer ${usuario?.token}`,
            'refresh-token': usuario?.detailToken?.refreshToken ?? '',
            refresh_token: usuario?.detailToken?.refreshToken ?? '',
          },
        })

        //! El error del REINTENTO no vuelve a pasar por el `catchError` de `intercept`: ese ya se
        //! disparó y RxJS no lo reengancha. Sin este segundo candado, una petición que sigue dando
        //! 401 después de renovar se quedaba sin cerrar la sesión — el usuario en un limbo, con la
        //! pantalla montada y todo fallando en silencio. Lo encontró la prueba, no la lectura.
        return next.handle(reintento).pipe(
          catchError((errorDelReintento) => {
            if (errorDelReintento?.status === 401) this.cerrarSesion()
            return throwError(errorDelReintento)
          }),
        )
      }),
    )
  }

  private cerrarSesion(): void {
    //* El usuario no está autenticado o la sesión ha expirado
    this.authService.logout()
    //* F-145 · La pantalla en la que murió la sesión viaja en `returnUrl` para volver a ella tras el
    //* login. Es el caso más común del botón del correo del comité: se abre días después, el token
    //* venció, el refresco falla y sin esto el votante aterrizaba en su landing y no en la sesión.
    //* Si ya se está en `/login` no se agrega: volver al login desde el login es un bucle.
    const actual = this.router.url || ''
    const returnUrl = actual && !actual.startsWith('/login') ? actual : null
    this.router.navigate(['/login'], returnUrl ? { queryParams: { returnUrl } } : undefined)
  }
}
